import { defineFeature } from '../define-feature';
import { TEST_IDS, sidebarNavTestId } from '../test-ids';

export default defineFeature({
  id: 'sales.payment.list',
  category: 'sales',
  title: 'Consulter et gérer les paiements',
  intent:
    'Consulter la liste de tous vos paiements, les filtrer, voir leur détail, et agir dessus : rembourser, gérer une empreinte bancaire, ajuster un montant ou déclarer un encaissement manuel.',
  context: {
    why: "La liste des paiements est votre vue d'ensemble sur les encaissements. On y retrouve un paiement, on vérifie qu'un client a bien réglé, on rembourse après une annulation, et on rapproche les entrées d'argent de sa comptabilité. C'est aussi de là qu'on agit sur les cas particuliers : capturer une empreinte bancaire après un no-show, ajuster un montant, ou déclarer qu'une réservation a été payée sur place, sans passage en ligne.",
    use_cases: [
      "Vérifier qu'un client a bien réglé sa prestation.",
      "Rembourser un client après l'annulation de son rendez-vous.",
      'Marquer une réservation comme payée sur place (encaissement manuel).',
      'Capturer une empreinte bancaire en cas de no-show.',
    ],
  },
  required_scopes: ['sales.payment.read'],
  ui_entry_points: [{ route: '/sales', element: 'Écran « Paiements » de Mes ventes' }],
  api: {
    primary_operation_id: 'ProPaymentAppController_listProPayments',
    related_operation_ids: [
      'ProPaymentAppController_getProPayment',
      'ProPaymentAppController_refundProPayment',
      'ProPaymentAppController_captureProPayment',
      'ProPaymentAppController_cancelProPayment',
      'ProPaymentAppController_updateProPaymentAmount',
      'ProPaymentAppController_declareReservationManualPaid',
      // Envoi du lien d'une demande en attente au client (email / SMS) et canaux proposés.
      'ProPaymentAppController_sendProPaymentLink',
      'ProPaymentAppController_getLinkChannels',
      // Ventes d'un client donné (onglet « Ventes » de sa fiche) : rattaché ici et non au
      // manifeste `customers`, dont les `required_scopes` sont `customer.read` — un APPRENTICE
      // y verrait une opération qui lui répond 403.
      'ProCustomerAppController_getProCustomerPayments',
    ],
  },
  steps: [
    '**Ouvrez « Mes ventes »**, écran « Paiements ».',
    '**Filtrez par type de paiement, par statut, par date ou par montant** pour réduire la liste.',
    '**Ouvrez un paiement** pour voir son détail : client, rendez-vous concerné, montant, statut et virement estimé vers votre banque.',
    '**Agissez si besoin** : rembourser, capturer ou annuler une empreinte, ajuster le montant, ou déclarer un encaissement manuel.',
  ],
  gotchas: [
    "**Les paiements en ligne passent par Stripe.** La liste reste consultable sans compte Stripe (encaissements déclarés sur place, historique), mais un bandeau vous rappelle que vos clients ne pourront pas payer en ligne tant que le compte n'est pas connecté et actif.",
    '**Un remboursement est définitif.** Une fois lancé, on ne revient pas dessus : vérifiez le montant et le bon paiement avant de confirmer.',
    "**« Déclarer un paiement manuel » enregistre un encaissement sur place.** C'est le moyen de tracer un règlement fait hors ligne (espèces, carte au comptoir) pour que votre suivi reste complet.",
    '**Les types de paiement viennent de la configuration de la prestation.** Sauvegarde de carte, empreinte avec blocage, paiement intégral, acompte : ce qui apparaît ici découle de ce que vous avez réglé dans le Catalogue, prestation par prestation.',
    "**Le lien d'une demande en attente s'envoie au client par email ou par SMS**, depuis le détail du paiement ou la fenêtre « Partager le lien » (la case « Envoyer le lien au client » le fait dès la création). Le message envoyé et les canaux proposés se règlent dans Marketing › Messages automatiques › « Demande de paiement » ; un SMS consomme votre quota.",
    "**L'empreinte bancaire se capture ou s'annule.** En cas de no-show, vous pouvez capturer l'empreinte prise à la réservation ; sinon, vous l'annulez pour libérer le client.",
    "**« Virement envoyé à votre banque » est une estimation.** Elle croise la date de disponibilité des fonds chez Stripe et la fréquence de virement de votre compte ; votre banque peut mettre quelques jours ouvrés à créditer le virement. Rien ne s'affiche pour les paiements antérieurs à cette information, les encaissements déclarés sur place, les paiements remboursés, ni en virement manuel ou virements suspendus. Le suivi réel des virements est dans Entreprise › Comptes de paiements.",
  ],
  related_features: [
    'establishment.payment.stripe_connect',
    'catalog.service.manage',
    'agenda.appointment.status',
    'establishment.payment.payouts',
  ],
  demo_script: {
    // Les lignes viennent du jeu de ventes de `backend:seed-e2e` (paiements sans
    // identifiant Stripe : la liste comme le détail restent hors-ligne en CI).
    duration_target_s: 30,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption:
          "La liste des paiements, c'est votre vue d'ensemble sur tous vos encaissements : voyons comment la consulter.",
      },
      {
        action: 'click',
        target: sidebarNavTestId('/sales'),
        caption: 'Menu latéral, « Mes ventes », onglet Paiements : tous vos encaissements réunis au même endroit.',
        capture: true,
      },
      { action: 'click', target: TEST_IDS.list_row, caption: 'Ouvrez un paiement pour en voir le détail.' },
      // `wait_for` avant la capture : en profil `fast` le runner enchaîne sans
      // battement, la capture partirait avant l'ouverture de la modale.
      {
        action: 'wait_for',
        target: TEST_IDS.payment_detail_modal,
        caption: 'Client, réservation, montant et statut : tout le paiement en un coup d’œil.',
        capture: true,
      },
    ],
  },
});

// 💬 Verbatim Kaltouma
// > (Autonome, ancré code.) Liste des paiements réalisés avec filtres (type de paiement, statut) et
// > recherche ; détail ; remboursement ; gestion d'empreinte ; déclaration d'un paiement manuel.
